import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Breadcrumbs, Container, PageFade } from "../components/site/Motion";
import { Field, SelectField } from "../components/site/FormBits";
import { ConsentBoxes } from "../components/site/ConsultationSection";
import { findSector, findJob } from "../data/sectors";
import { STATUT, ANCIENNETE, BENEFICE, EMPLOYES, OBJECTIF, isValidEmail, isValidPhone, formatPhone } from "../data/options";
import { api, apiError, store } from "../lib/api";
import NotFound from "./NotFound";

const EMPTY = { full_name: "", company: "", phone: "", email: "", statut: "", anciennete: "", benefice: "", employes: "",
  objectif: "", objectif_autre: "", consent_data: false, consent_marketing: false, website: "" };
const STEPS = ["Vos coordonnées", "Votre profil", "Votre objectif"];

function validateStep(step, f) {
  const e = {};
  if (step === 0) {
    if (f.full_name.trim().length < 2) e.full_name = "Veuillez indiquer votre nom complet.";
    if (!isValidPhone(f.phone)) e.phone = "Veuillez entrer un numéro canadien à 10 chiffres, ex. (514) 123-4567.";
    if (!isValidEmail(f.email)) e.email = "Veuillez entrer une adresse courriel valide.";
  }
  if (step === 1) {
    if (!f.statut) e.statut = "Veuillez sélectionner votre statut professionnel.";
    if (!f.anciennete) e.anciennete = "Veuillez sélectionner une option.";
    if (!f.benefice) e.benefice = "Veuillez sélectionner une option.";
    if (!f.employes) e.employes = "Veuillez sélectionner une option.";
  }
  if (step === 2) {
    if (!f.objectif) e.objectif = "Veuillez sélectionner votre objectif principal.";
    if (f.objectif === "autre" && f.objectif_autre.trim().length < 2) e.objectif_autre = "Veuillez préciser votre objectif.";
    if (!f.consent_data) e.consent_data = "Votre consentement est requis pour poursuivre.";
  }
  return e;
}

const Progress = ({ step }) => (
  <div className="mb-8" data-testid="questionnaire-progress">
    <div className="flex items-center justify-between text-xs text-[#242020]/60">
      <span data-testid="questionnaire-step-label">Étape {step + 1} sur {STEPS.length} — {STEPS[step]}</span>
    </div>
    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#EDE3D9]">
      <motion.div className="h-full rounded-full bg-[#650D23]" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.4 }} />
    </div>
  </div>
);

const StepContact = ({ f, set, err }) => (
  <div className="space-y-5">
    <Field label="Nom complet" required error={err.full_name} htmlFor="q-name" errorTestid="q-error-full_name">
      <input id="q-name" className="field-input" placeholder="Votre nom complet" value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} aria-invalid={!!err.full_name} data-testid="q-full-name-input" />
    </Field>
    <Field label="Nom de votre entreprise" htmlFor="q-company">
      <input id="q-company" className="field-input" placeholder="Nom de l'entreprise (si applicable)" value={f.company} onChange={(e) => set("company")(e.target.value)} data-testid="q-company-input" />
    </Field>
    <Field label="Numéro de téléphone" required error={err.phone} htmlFor="q-phone" errorTestid="q-error-phone">
      <input id="q-phone" type="tel" inputMode="tel" className="field-input" placeholder="(514) 123-4567" value={f.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} aria-invalid={!!err.phone} data-testid="q-phone-input" />
    </Field>
    <Field label="Adresse courriel" required error={err.email} htmlFor="q-email" errorTestid="q-error-email">
      <input id="q-email" type="email" className="field-input" placeholder="vous@exemple.com" value={f.email} onChange={(e) => set("email")(e.target.value)} aria-invalid={!!err.email} data-testid="q-email-input" />
    </Field>
  </div>
);

const StepProfile = ({ f, set, err }) => {
  const salarie = f.statut === "salarie";
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field label="Quel est votre statut professionnel?" required error={err.statut} htmlFor="q-statut" errorTestid="q-error-statut">
          <SelectField id="q-statut" value={f.statut} onChange={set("statut")} options={STATUT} error={err.statut} testid="q-statut-select" />
        </Field>
      </div>
      <Field label="Depuis combien d'années êtes-vous en affaires?" required error={err.anciennete} htmlFor="q-anc" errorTestid="q-error-anciennete">
        <SelectField id="q-anc" value={f.anciennete} onChange={set("anciennete")} options={ANCIENNETE} error={err.anciennete} testid="q-anciennete-select" />
      </Field>
      <Field label={salarie ? "Quel est votre revenu net mensuel approximatif?" : "Quel est votre bénéfice net mensuel approximatif?"} required error={err.benefice} htmlFor="q-ben" errorTestid="q-error-benefice">
        <SelectField id="q-ben" value={f.benefice} onChange={set("benefice")} options={BENEFICE} error={err.benefice} testid="q-benefice-select" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Avez-vous cinq employés ou entrepreneurs ou plus au sein de votre entreprise?" required error={err.employes} htmlFor="q-emp" errorTestid="q-error-employes">
          <SelectField id="q-emp" value={f.employes} onChange={set("employes")} options={EMPLOYES} error={err.employes} testid="q-employes-select" />
        </Field>
      </div>
    </div>
  );
};

const StepGoal = ({ f, set, err }) => (
  <div className="space-y-6">
    <Field label="Quel est votre principal objectif actuellement?" required error={err.objectif} htmlFor="q-obj" errorTestid="q-error-objectif">
      <SelectField id="q-obj" value={f.objectif} onChange={set("objectif")} options={OBJECTIF} error={err.objectif} testid="q-objectif-select" />
    </Field>
    {f.objectif === "autre" && (
      <Field label="Précisez votre objectif" required error={err.objectif_autre} htmlFor="q-autre" errorTestid="q-error-objectif_autre">
        <input id="q-autre" className="field-input" value={f.objectif_autre} onChange={(e) => set("objectif_autre")(e.target.value)} aria-invalid={!!err.objectif_autre} data-testid="q-objectif-autre-input" />
      </Field>
    )}
    <p className="rounded-xl bg-[#EDE3D9]/50 p-4 text-xs text-[#242020]/75">Nous ne vous demanderons jamais votre NAS, vos coordonnées bancaires ni d'autres informations financières sensibles.</p>
    <ConsentBoxes f={f} set={set} err={err} prefix="q" />
  </div>
);

export default function Questionnaire() {
  const { sectorSlug, jobSlug } = useParams();
  const nav = useNavigate();
  const sector = findSector(sectorSlug);
  const job = findJob(sector, jobSlug);
  const [f, setF] = useState(() => ({ ...EMPTY, ...store.get("struktiv_q", {}), consent_data: false, website: "" }));
  const [step, setStep] = useState(() => store.get("struktiv_q_step", 0));
  const [err, setErr] = useState({});
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const { consent_data, website, ...rest } = f;
    store.set("struktiv_q", rest);
  }, [f]);
  useEffect(() => {
    store.set("struktiv_q_step", step);
  }, [step]);

  if (!sector || !job) return <NotFound />;

  const set = (k) => (v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "statut" && v !== "salarie" && p.anciennete === "na") next.anciennete = "";
    return next;
  });

  const next = async () => {
    const e = validateStep(step, f);
    setErr(e);
    if (Object.keys(e).length) return;
    if (step < STEPS.length - 1) return setStep(step + 1);
    setSending(true);
    setServerError("");
    try {
      const { data } = await api.post("/leads", { ...f, secteur: sector.slug, secteur_label: sector.name, metier: job.slug, metier_label: job.name });
      store.set("struktiv_result", { ...data, statut: f.statut, objectif: f.objectif, metier_label: job.name, secteur_label: sector.name });
      store.set("struktiv_q_step", 0);
      nav("/resultat");
    } catch (x) {
      setServerError(apiError(x));
    } finally {
      setSending(false);
    }
  };

  const StepView = [StepContact, StepProfile, StepGoal][step];
  return (
    <PageFade>
      <section className="wave-bg relative">
        <Container className="grid gap-10 py-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14 lg:py-14">
          <div className="space-y-6">
            <Breadcrumbs items={[{ label: "Accueil", to: "/" }, { label: sector.name, to: `/secteurs/${sector.slug}` }, { label: job.name }]} />
            <div className="flex items-center gap-4" data-testid="questionnaire-selected-job">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#650D23] text-[#FFFDFB]"><sector.icon className="h-6 w-6" strokeWidth={1.5} /></span>
              <div><p className="text-xs uppercase tracking-widest text-[#242020]/60">{sector.name}</p><p className="font-display text-2xl">{job.name}</p></div>
            </div>
            <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Parlez-nous <span className="text-[#650D23]">de vous.</span></h1>
            <p className="max-w-md text-lg text-[#242020]/80">Répondez à quelques questions pour découvrir les solutions financières qui pourraient correspondre à votre réalité professionnelle.</p>
            <img src={job.image} alt="" className="hidden aspect-[4/5] w-full max-w-md rounded-[2rem] object-cover lg:block" />
          </div>
          <div className="rounded-[1.6rem] border border-[#EDE3D9] bg-[#FAF6F1] p-6 shadow-[0_30px_60px_-40px_rgba(74,12,27,.45)] sm:p-10 lg:self-start" data-testid="questionnaire-card">
            <Progress step={step} />
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                <StepView f={f} set={set} err={err} />
              </motion.div>
            </AnimatePresence>
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} className="hidden" aria-hidden="true" />
            {serverError && <p className="mt-5 text-sm text-[#b42318]" role="alert" data-testid="q-server-error">{serverError}</p>}
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              {step > 0 ? (
                <button onClick={() => { setErr({}); setStep(step - 1); }} className="inline-flex items-center gap-2 text-sm text-[#650D23]" data-testid="q-back-button">
                  <ArrowLeft className="h-4 w-4" /> Retour
                </button>
              ) : <span />}
              <button onClick={next} disabled={sending} className="btn-primary sm:min-w-[240px]" data-testid={step === 2 ? "q-submit-button" : "q-next-button"}>
                {step === 2 ? (sending ? "Analyse en cours…" : "VOIR MON FORFAIT") : "Continuer"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Container>
      </section>
    </PageFade>
  );
}
